import React, {useEffect, useRef, useState} from "react";
import TextEdit from "../../organisms/textEdit/textEdit";
import {observer} from "mobx-react-lite";
import {Col, Container, Row} from "reactstrap";
import "./createEditEventTemplate.sass";
import Selection from "../../organisms/selection/selection";
import EventPriceEdit from "../../organisms/eventPriceEdit/eventPriceEdit";
import EventBookingDeadlineEdit from "../../organisms/eventBookingDeadlineEdit/eventBookingDeadlineEdit";
import {
  CustomLocationConstant,
  VirtualEventConstant
} from "../../../stores/domainStore/pages/models/eventDetails";
import EventStartAndEndEdit from "../../organisms/eventStartAndEndEdit/eventStartAndEndEdit";
import {useHistory} from "react-router";
import {useTranslation} from "react-i18next";
import CheckBox from "../../atoms/checkBox/checkBox";
import {hasSpaces} from "../../../utils/stringUtils";
import CreateEditForm from "../../molecules/createEditForm/createEditForm";
import ButtonSelector from "../../organisms/buttonSelector/buttonSelector";
import FadeContent from "../../molecules/fadeContent/fadeContent";
import Skeleton from "react-loading-skeleton";
import ButtonIcon from "../../atoms/button/buttonIcon";
import EventBookingSpaceEdit from "../../organisms/eventBookingSpacesEdit/eventBookingSpaceEdit";
import {
  villageCentre,
  EditThisSeriesFeatureName,
  independentAndServicedApartmentResidents
} from "../../../shared/constants";
import {useVillageFeatureToggle} from "../../../utils/featureToggle/villageFeatureToggle";
import MultiVillageSelect from "../../molecules/multiVillageSelect/multiVillageSelect";
import Tabs, {Tab} from "../../molecules/tabs/tabs";
import {format} from "date-fns";
import {IconSource} from "../../../shared/enums/iconSource";
import Banner from "../../atoms/banner/banner";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import {useStores} from "../../../stores/useStores";
import {Action, Path} from "history";
import {RoutePaths} from "../../../shared/routePaths";
import {runInAction} from "mobx";
import {Navigation} from "../../../utils/navigation";
import ManageBookingsTemplate from "../bookings/manageBookingsTemplate";
import {EventStatus} from "../../../shared/enums/eventStatus";
import Icon from "../../atoms/common/icon/icon";
import {EventDetailsHelper} from "../../../shared/eventDetailsHelper";
import {isFeatureToggleEnabled} from "../../../utils/featureToggle/featureToggle";
import ViewEventTemplate from "./viewEvent/viewEventTemplate";
import EventStartAndEndView from "../../organisms/eventStartAndEndView/eventStartAndEndView";
import EventRecurrenceEdit from "../../organisms/eventRecurrenceEdit/eventRecurrenceEdit";
import {QueryParams} from "../../../shared/enums/queryParams";
import {utcToZonedTime} from "date-fns-tz";
import ImageSelector from "../../molecules/imageSelector/imageSelector";
import {BreadcrumbItem} from "../../atoms/breadcrumb/breadcrumb";
import EventPreReleaseEdit from "../../organisms/eventPreReleaseEdit/eventPreReleaseEdit";
import EventCreateEditPillView from "../../organisms/eventCreateEditPillView/eventCreateEditPillView";
import {RecurringStatus} from "../../../shared/enums/RecurringStatus";
import {useAsync} from "react-async";
import Tag from "../../atoms/tag/tag";

export interface ICreateEditEventTemplateProps {
  publishAction: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  isEditing: boolean;
  isCancellingPostponing?: boolean;
  deleteAction: () => void;
  deleteSeriesAction?: () => void;
  editAction?: () => void;
  viewAction?: () => void;
  openCancelPostponeModalAction?: (eventStatus: EventStatus) => void;
  saveCancelledPostponedEventAction?: () => void;
  isEditingEventSeries?: boolean;
  isLoadingEventDetails?: boolean;
  selectedEventTemplateRef?: string;
  onChangeTemplate: () => void;
  loadEventBookingDetails?: (eventId: string) => void;
}

const CreateEditEventTemplate: React.FC<ICreateEditEventTemplateProps> = ({
  isSaving,
  isDeleting,
  isCancellingPostponing = false,
  isEditing,
  publishAction,
  deleteAction,
  deleteSeriesAction,
  editAction,
  viewAction,
  openCancelPostponeModalAction = () => {},
  saveCancelledPostponedEventAction,
  isLoadingEventDetails = false,
  selectedEventTemplateRef,
  onChangeTemplate,
  loadEventBookingDetails
}) => {
  const {
    uiStore: {modalStore, eventsUiStore, eventCatalogueUiStore, accountStore},
    domainStore: {eventsDomainStore, villageCalendarsDomainStore}
  } = useStores();

  const hasUnsavedChanges = eventsDomainStore.editableEvent.hasChanges;
  const eventDetails = eventsDomainStore.editableEvent;

  useEffect(() => {
    window.scrollTo(0, 0);
    eventDetails.setMaxDeadlineSelectValues(
      eventDetails.eventDateDetails.startDate.date
    );
    // Populate the CMS calendar → personTypeIds lookup used to default a
    // new event's audience to the full set of person types (e.g. IA + SA).
    // Non-fatal: if it fails, the integration API's single
    // VillageCalendar.personType is used as a fallback.
    // Always refresh so calendarType / personTypeIds edits made on the
    // Village Calendars admin page are picked up when creating an event.
    if (villageCalendarsDomainStore) {
      villageCalendarsDomainStore.getVillageCalendars().catch(() => undefined);
    }
  }, []);

  const {t} = useTranslation(["translation", "event"]);

  const history = useHistory();
  const params = new URLSearchParams(history.location.search);
  const templateId = params.get(QueryParams.TemplateId);
  //When we are using arrow buttons to back and forth the page, we need to set the state of the edit series to refresh the page.
  const isEditingEventSeries = params.get(QueryParams.EditSeries);

  //we are checking what's the eventId in the URL to get the eventDetails of the current event.
  //when moving back using backward button to detached occurrence from series page , we need to find eventId of detached occurrence.
  const eventIdInURL = history.location.pathname.split("events/")[1];

  const occurrenceDate = params.get(QueryParams.OccurrenceDate);

  // Declared before the reload effect so the reload uses the URL's occurrence date.
  useEffect(() => {
    eventsDomainStore.setOccurrenceDate(occurrenceDate);
  }, [eventsDomainStore, occurrenceDate]);

  //Navigating back to View page (past event) of current series from Edit this series Page.Clicked back arrow button, so eventDetails need to be reloaded.
  // Skipped on mount: the parent page has already loaded the event.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    if (isEditing) {
      const isNavigatingToAnotherEvent = eventIdInURL !== eventDetails.id;

      if (
        isNavigatingToAnotherEvent ||
        eventDetails.recurringStatus !== RecurringStatus.DetachedOccurrence
      )
        eventsDomainStore.loadEditableEvent(eventIdInURL, false);
    }
  }, [eventsDomainStore, isEditingEventSeries, eventIdInURL, occurrenceDate]);

  isEditingEventSeries && isEditing
    ? eventsUiStore.setEditingEventSeries(true)
    : eventsUiStore.setEditingEventSeries(false);

  useEffect(() => {
    if (templateId) {
      eventCatalogueUiStore.loadEventTemplate(templateId);
    }
  }, [eventCatalogueUiStore, templateId]);

  useEffect(() => {
    runInAction(() => {
      eventsUiStore.isCreatingOrEditing = true;
    });
  }, [eventsUiStore, eventsUiStore.isLoading]);

  const editThisSeriesFeatureEnabled = isFeatureToggleEnabled(
    EditThisSeriesFeatureName
  );

  const isCancellableEvent = !!eventDetails.isReleased;
  const isViewingOrEditingSeries =
    eventsUiStore.isEditingEventSeries || eventsUiStore.isViewingEventSeries();

  const shouldShowEditOrViewButton =
    !isViewingOrEditingSeries &&
    isEditing &&
    EventDetailsHelper.canEditEventSeries(eventDetails); // if the event is a series type

  const shouldShowCancelOrPostponeButton =
    eventsUiStore.isEnhancedCalendar &&
    isEditing &&
    isCancellableEvent &&
    // Ensure event has not yet ended
    !eventDetails.isPassedEndDate &&
    !EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails);

  const shouldShowDeleteButton =
    isEditing &&
    !eventsUiStore.isEditingEventSeries &&
    // Ensure event has not yet started
    eventDetails.isBeforeStartDate &&
    // Cancelled event with bookings cannot be deleted
    !(
      EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails) &&
      (eventDetails.bookingSpacesDetails.totalBookings > 0 ||
        eventDetails.bookingSpacesDetails.highestBookingCountForSeries > 0)
    ) &&
    // User needs permission to delete released Enhanced calendar events.
    ((eventDetails.isReleased &&
      accountStore.abilities.can("Delete", "ReleasedEvent")) ||
      !eventDetails.isReleased ||
      !eventsUiStore.isEnhancedCalendar);

  const shouldShowDeleteSeriesButton =
    isEditing && eventsUiStore.isEditingEventSeries;

  const currentTime = utcToZonedTime(
    new Date(),
    eventDetails.timeZone
  ).getTime();
  const eventRecurrenceEndDateInPast =
    eventDetails.eventRecurrenceDetails.recurrenceEndDate.date?.getTime()! <
    currentTime;

  const buttons = (
    <>
      {editThisSeriesFeatureEnabled && shouldShowEditOrViewButton && (
        //Edit or View series button
        <div className="edit-view-event-button-wrapper">
          <ButtonIcon
            type={"secondary"}
            onClick={() => {
              if (eventRecurrenceEndDateInPast) {
                viewAction && viewAction();
              } else {
                // The series is loaded by the URL-change effect, so a blocked navigation doesn't discard edits.
                editAction && editAction();
              }
            }}
            isDisabled={isSaving || isDeleting}
          >
            {eventRecurrenceEndDateInPast
              ? t("event:actions.viewSeries")
              : t("event:actions.editSeries")}
          </ButtonIcon>
        </div>
      )}
      {shouldShowDeleteButton && (
        //Delete button
        <div className="delete-event-button-wrapper">
          <ButtonIcon
            type={
              eventDetails.isReleased && eventsUiStore.isEnhancedCalendar
                ? "primary-negative"
                : "secondary-negative"
            }
            onClick={deleteAction}
            isDisabled={isSaving || isDeleting}
            isBusy={isDeleting}
          >
            {t("event:actions.delete")}
          </ButtonIcon>
        </div>
      )}
      {shouldShowDeleteSeriesButton && (
        <div className="delete-event-button-wrapper">
          <ButtonIcon
            type={"secondary-negative"}
            onClick={deleteSeriesAction}
            isDisabled={isSaving || isDeleting}
            isBusy={isDeleting}
          >
            {t("event:actions.deleteSeries")}
          </ButtonIcon>
        </div>
      )}
      {
        // Cancel button
        shouldShowCancelOrPostponeButton && (
          <div className="cancel-postpone-button-wrapper">
            <ButtonIcon
              type="secondary-negative"
              onClick={() =>
                openCancelPostponeModalAction?.(EventStatus.Cancelled)
              }
              isDisabled={isSaving || isCancellingPostponing}
              isBusy={
                isCancellingPostponing || eventsUiStore.isLoadingEventBookings
              } // wait for fully loaded bookings in order to display the correct numbers
            >
              {eventsUiStore.isEditingEventSeries
                ? t("event:actions.cancelSeries")
                : t("event:actions.cancel")}
            </ButtonIcon>
          </div>
        )
      }
      {!EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails) ? (
        (!eventDetails.isPassedEndDate ||
          eventsUiStore.isEditingEventSeries) && (
          /***
           * Non-cancponed event's publish button
           */
          <div>
            <ButtonIcon
              type="primary"
              onClick={() => {
                setDefaultsBeforePublish();
                if (eventDetails.hasChangesIgnoringRecurrenceEndDate) {
                  eventDetails.setIsDirty(true);
                }
                if (eventsUiStore.canPublish()) {
                  publishAction();
                }
              }}
              isDisabled={!eventsUiStore.canPublish() || isSaving || isDeleting}
              isBusy={isSaving}
            >
              {t("translation:actions.publish")}
            </ButtonIcon>
          </div>
        )
      ) : (
        /***
         * Cancponed event's publish button
         */
        <div>
          <ButtonIcon
            type="primary"
            onClick={() => {
              eventDetails.setIsDirty(true);
              if (
                eventDetails.canUpdateCancelledPostponedEvent &&
                eventDetails.reason.isValid
              ) {
                saveCancelledPostponedEventAction?.();
              }
            }}
            isDisabled={
              !eventsUiStore.canPublish() ||
              isSaving ||
              isDeleting ||
              !eventDetails.reason.isValid
            }
            isBusy={isSaving}
          >
            {t("translation:actions.publish")}
          </ButtonIcon>
        </div>
      )}
    </>
  );
  const getImageValidationMessage = (): string => {
    if (eventDetails.image.hasExceedsFileSizeLimit)
      return t("translation:controls.image.overFileSizeLimit", {
        maxImageFileSizeMegabytes: 5
      });
    else if (eventDetails.image.isInvalidFileFormat)
      return t("translation:controls.image.invalidFileFormat");
    else if (eventDetails.image.hasExceedsResolution)
      return t("translation:controls.image.aboveMaxResolution", {
        maxWidthAndHeight: 4000
      });
    else if (!eventDetails.image.file)
      return t("translation:controls.image.error");
    return "";
  };

  useEffect(() => {
    if (
      !isEditing &&
      eventDetails.bookingDetails.hasBookingDeadline &&
      !eventDetails.bookingDetails.datesAreDirty
    ) {
      eventDetails.bookingDetails.hours.setSelectedItem("1");
    }
  }, [
    isEditing,
    eventDetails.bookingDetails.hasBookingDeadline,
    eventDetails.bookingDetails.datesAreDirty,
    eventDetails.bookingDetails.hours
  ]);

  React.useEffect(() => {
    const unblock = history.block((path: Path, action: Action) => {
      if (hasUnsavedChanges || eventsUiStore.hasUnsavedBookings) {
        // If the modal is open and the user hit the back button on the browser, unblock the navigation.
        if (modalStore.isOpen && action === Action.Pop) {
          unblock();
          return true;
        }
        let modalMessage = {
          contentMessage: hasUnsavedChanges
            ? t("translation:messages.cancelCreateOrEdit", {
                target: t("event:event")
              })
            : t("translation:messages.isProcessingBookings"),
          title: hasUnsavedChanges
            ? t("translation:titles.unsavedChanges")
            : t("translation:titles.processing")
        };
        modalStore.openModalWithContent({
          content: modalMessage.contentMessage,
          title: modalMessage.title,
          primaryButtonText: t("translation:confirmations.affirmativeLeave"),
          secondaryButtonText: t("translation:confirmations.negative"),
          canToggle: false,
          dangerButton: true,
          centered: true,
          onPrimaryClick: () => {
            unblock();
            const destination = new URL(
              typeof path === "string" ? path : history.createHref(path),
              window.location.origin
            );
            const destinationParams = destination.searchParams;
            if (
              destination.pathname === history.location.pathname &&
              destinationParams.get(QueryParams.EditSeries) ===
                isEditingEventSeries &&
              destinationParams.get(QueryParams.OccurrenceDate) ===
                occurrenceDate
            ) {
              eventsUiStore.reloadEventDetailsAndBookings();
            }
            history.push(path);
            modalStore.toggle();
          },
          onSecondaryClick: modalStore.toggle
        });
        return false;
      }
    });
    return () => {
      unblock();
    };
  }, [
    eventsUiStore,
    hasUnsavedChanges,
    history,
    modalStore,
    t,
    isEditingEventSeries,
    occurrenceDate
  ]);

  const hasBookings = eventDetails.bookingSpacesDetails.totalBookings > 0;
  const isFixedPrice =
    eventDetails.priceDetails.paidType.text === EventPaidType.fixed;
  const isEditingAReleasedEvent = isEditing && eventDetails.isReleased;

  const isReadOnly =
    eventDetails.isPassedEndDate ||
    (eventDetails.isMultiVillage &&
      !accountStore.abilities.can("Update", "MultiVillage"));

  const shouldShowEventDateEdit =
    // Always allow editing event series start/end time.
    (EventDetailsHelper.isSeries(eventDetails) &&
      eventsDomainStore.canMakeNonCosmeticChanges()) ||
    // Allow changing start/end date of unreleased  one off events.
    (!EventDetailsHelper.isSeries(eventDetails) && !eventDetails.isReleased) ||
    // Allow editing start/end date of recurring event occurrence
    eventsDomainStore.occurrenceDate;

  const shouldShowEventDateReadOnly =
    isEditingAReleasedEvent && !eventsUiStore.isEditingEventSeries;

  const requiresBookingCheckBoxHelpText = eventsUiStore.isEditingEventSeries
    ? ""
    : t("event:labels.booking.hasBookingDeadlineHelpTextAfterRelease");

  const showDeadLinePassedBanner =
    eventDetails.isDeadLinePassed && !eventsUiStore.isEditingEventSeries;

  const showEditingSingleOccurrenceOfSeriesBanner =
    eventsDomainStore.occurrenceDate &&
    isEditing &&
    !eventsUiStore.isEditingEventSeries;

  const showRecurringEventComponent =
    eventsUiStore.isEditingEventSeries || !isEditing;

  const disableRequiresBookingCheckbox =
    ((!eventsUiStore.isEditingEventSeries ||
      !eventsDomainStore.canMakeNonCosmeticChanges()) &&
      isEditingAReleasedEvent) ||
    hasBookings ||
    isFixedPrice;

  const isEnhanced = eventsUiStore.isEnhancedCalendar;
  const needsExplicitPersonTypes = !isEnhanced || eventDetails.isMultiVillage;

  const setDefaultsBeforePublish = () => {
    if (needsExplicitPersonTypes && !isEditing) {
      const personTypeIds =
        eventsUiStore.personTypeIdsBySelectedCalendarName;
      if (personTypeIds.length > 0) {
        eventDetails.personTypes = [...personTypeIds];
      }
    }

    if (!isEnhanced) {
      const villageCentreLocation = eventDetails.location.items.find(
        (loc) => loc.label === villageCentre
      );
      if (villageCentreLocation) {
        eventDetails.location.setSelectedItem(villageCentreLocation.value);
      } else {
        eventDetails.location.setSelectedItem(
          eventDetails.location.items[0]?.value
        );
      }
    }
  };

  const imageSelect = useAsync({
    deferFn: (args) =>
      eventsDomainStore.editableEvent.image.setImageFromUpload(args[0])
  });

  const disableCostAllocation = (() => {
    if (eventDetails.releaseDate.date) {
      // Pre-release events cannot edit cost allocation after the booking deadline has closed
      return eventDetails.isDeadLinePassed;
    }
    // Normal events can always edit the cost allocation.
    // Once the event has finished - the event is considered "read only", which prevents editing anything at all.
    return false;
  })();

  const formBody = isLoadingEventDetails ? (
    <Skeleton className="placeholder" />
  ) : // If the event is read only then show the uneditable fields
  isReadOnly ? ( // after endDate
    <ViewEventTemplate
      eventDetail={eventDetails}
      isLoading={false}
      isSaving={isSaving}
      isViewingEventSeries={eventsUiStore.isViewingEventSeries()}
      shouldShowCancelOrPostponeButton={shouldShowCancelOrPostponeButton}
      openCancelPostponeModalAction={openCancelPostponeModalAction}
      isReadOnly={isReadOnly}
    />
  ) : EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails) ? (
    <ViewEventTemplate
      eventDetail={eventDetails}
      isLoading={isLoadingEventDetails}
      isSaving={isSaving}
      isViewingEventSeries={eventsUiStore.isViewingEventSeries()}
      shouldShowCancelOrPostponeButton={shouldShowCancelOrPostponeButton}
      openCancelPostponeModalAction={openCancelPostponeModalAction}
      isCancelledOrPostponedEvent={true}
    />
  ) : (
    <div className="form-body">
      <FadeContent fadeContent={isEditing}>
        <Row className="pb-4">
          <Col>
            <ButtonSelector
              value={selectedEventTemplateRef}
              label={t("event:labels.template")}
              buttonText={t("event:labels.changeTemplate")}
              onClick={onChangeTemplate}
              isDisabled={isSaving || isDeleting}
            ></ButtonSelector>
          </Col>
        </Row>
        <Row className="form-separator editMargin" />
      </FadeContent>
      <Row className="editMargin">
        <Container fluid>
          {showEditingSingleOccurrenceOfSeriesBanner && (
            <Row>
              <Col>
                <div className={"banner-padding"}>
                  <Banner
                    text={t(
                      "event:messages.editingOrViewingOccurrenceOfEventSeries"
                    )}
                    icon={IconSource.info}
                  />
                </div>
              </Col>
            </Row>
          )}

          {showDeadLinePassedBanner && (
            <Banner
              text={t("event:messages.editedEventPassesDeadline")}
              icon={IconSource.info}
            />
          )}

          <Row>
            <Col>
              <TextEdit
                type="text"
                value={eventDetails.title}
                validationMessage={t("event:errors.title")}
                label={t("event:labels.title")}
                placeholder={t("event:placeholders.title")}
              />
            </Col>
            <Col>
              <Selection
                value={eventDetails.category}
                label={t("event:labels.category")}
                validationMessage={t("event:errors.category")}
                placeholder={t("event:placeholders.selectCategory")}
                isLoading={false}
                isRequired={false}
              />
            </Col>
          </Row>
        </Container>
        <Col />
      </Row>
      <Row>
        <Col className="pb-4">
          <CheckBox
            value={eventDetails.isFeatured.value}
            onValueChange={(value) => eventDetails.isFeatured.setBoolean(value)}
            helpText={
              isEnhanced
                ? t("event:labels.featureHelpText")
                : t("event:labels.specialHelpText")
            }
            label={
              isEnhanced
                ? t("event:labels.isFeatured")
                : t("event:labels.special")
            }
          />
        </Col>
        <Col />
      </Row>
      {(shouldShowEventDateReadOnly || shouldShowEventDateEdit) && (
        <>
          <Row className="form-separator editMargin" />
          <div className="editMargin startAndEnd">
            {shouldShowEventDateReadOnly ? (
              <EventStartAndEndView
                eventDetails={eventDetails}
                isDisabled={isSaving}
                isCancellingPostponing={isCancellingPostponing}
                openCancelPostponeModalAction={openCancelPostponeModalAction}
                shouldShowCancelOrPostponeButton={
                  shouldShowCancelOrPostponeButton
                }
                isEditingSingleOccurrence={
                  eventsDomainStore.occurrenceDate !== undefined
                }
                isEndTimeVisible={isEnhanced}
              />
            ) : (
              <EventStartAndEndEdit
                value={eventDetails.eventDateDetails}
                label={t("event:labels.start")}
                isRecurringEvent={EventDetailsHelper.isSeries(eventDetails)}
                isEditing={isEditing}
                isReleased={eventDetails.isReleased}
                onStartDateChange={eventDetails.setMaxDeadlineSelectValues}
                isEnhancedCalendar={isEnhanced}
              />
            )}
          </div>
        </>
      )}
      {showRecurringEventComponent && (
        <>
          <Row className="form-separator editMargin" />
          <EventRecurrenceEdit
            eventStartAndEndDate={eventDetails.eventDateDetails}
            eventRecurrenceDetails={eventDetails.eventRecurrenceDetails}
            isEnhanced={isEnhanced}
            isReleased={eventDetails.isReleased}
            nextReleaseStartDate={eventsDomainStore.getNextReleasePeriodStartDate()}
            isEditing={isEditing}
          />
        </>
      )}

      {eventDetails.isMultiVillage && (
        <>
          <Row className="form-separator editMargin" />
          <Row className="editMargin">
            <Col>
              <MultiVillageSelect
                villages={eventDetails.villages}
                calendarName={eventsUiStore.selectedCalendarName}
                allVillageOptionWarning={t(
                  "event:errors.allVillageOptionSelected"
                )}
                servicedApartmentExcludedWarning={t(
                  "event:errors.servicedApartmentVillagesExcluded",
                  {calendar: independentAndServicedApartmentResidents}
                )}
                invalidMinSelectedVillageErrorMessage={t(
                  "event:errors.invalidMinimumVillageSelected"
                )}
              />
            </Col>
          </Row>
        </>
      )}

      {isEnhanced && (
        <>
          <Row className="form-separator editMargin" />
          <Row className="editMargin">
            <Col>
              <Selection
                value={eventDetails.location}
                label={t("event:labels.location")}
                validationMessage={t("event:errors.location")}
                placeholder={t("event:placeholders.location")}
                isLoading={isLoadingEventDetails}
                isDisabled={eventDetails.isMultiVillage}
              />
            </Col>
            <Col>
              {eventDetails.location.selectedItem?.value ===
                CustomLocationConstant && (
                <TextEdit
                  type="text"
                  value={eventDetails.customLocation}
                  validationMessage={t("event:errors.customLocation")}
                  label={t("event:labels.customLocation")}
                  placeholder={t("event:placeholders.location")}
                />
              )}
              {eventDetails.location.selectedItem?.value ===
                VirtualEventConstant && (
                <TextEdit
                  type="text"
                  value={eventDetails.virtualLocation}
                  validationMessage={
                    eventDetails.virtualLocation?.text.length > 0
                      ? hasSpaces(eventDetails.virtualLocation.text)
                        ? t("event:errors.virtualLocationMustHaveNoSpaces")
                        : t("event:errors.virtualLocationMustBeHttp")
                      : t("event:errors.virtualLocation")
                  }
                  label={t("event:labels.virtualLocation")}
                  placeholder={t("event:placeholders.virtualLocation")}
                />
              )}
            </Col>
          </Row>
        </>
      )}

      {isEnhanced && (
        <>
          <Row className="form-separator editMargin" />
          <Row>
            <Col sm={12} className="editMargin">
              <EventPriceEdit
                value={eventDetails.priceDetails}
                onSelect={(type: string) => {
                  eventDetails.reinitializeBookingDeadlineOnPrice(
                    type,
                    !!eventDetails.isReleased,
                    isEditing
                  );
                }}
                isMultiVillageEvent={eventDetails.isMultiVillage}
                hasPaidAttendees={eventDetails.hasPaidAttendees}
                disableFixedPrice={isEditingAReleasedEvent && !isFixedPrice}
                disableNonFixedPricePaidType={
                  isEditingAReleasedEvent && isFixedPrice
                }
                disableCostAllocation={disableCostAllocation}
                isRequired={false}
              />
            </Col>
            <Col />
          </Row>
        </>
      )}

      {!eventDetails.isMultiVillage && (
        <>
          <Row className="form-separator editMargin" />
          <Row>
            <Col className="editMargin">
              <CheckBox
                testId={"RequiresBooking"}
                value={eventDetails.bookingDetails.hasBookingDeadline}
                label={t("event:labels.booking.hasBookingDeadline")}
                helpText={requiresBookingCheckBoxHelpText}
                onValueChange={(val: boolean) => {
                  eventDetails.bookingDetails.setHasBookingDeadline(val);
                  eventDetails.setMaxDeadlineSelectValues(
                    eventDetails.eventDateDetails.startDate.date
                  );
                }}
                disabled={disableRequiresBookingCheckbox}
              />

              {eventsUiStore.isEditingEventSeries &&
                (eventsDomainStore.editableEvent?.isReleased ?? false) &&
                eventDetails.bookingDetails.initialBookingDeadline !==
                  eventDetails.bookingDetails.hasBookingDeadline && (
                  <div className={"bookability-warning-message"}>
                    <Banner
                      text={t("event:errors.bookabilityWarningMessage")}
                      icon={IconSource.info}
                    />
                  </div>
                )}
            </Col>
          </Row>

          {isEnhanced && (
            <FadeContent
              fadeContent={!eventDetails.bookingDetails.hasBookingDeadline}
            >
              <Row>
                <Col>
                  <EventBookingSpaceEdit
                    eventBookingSpace={eventDetails.bookingSpacesDetails}
                    isEditingEventSeries={eventsUiStore.isEditingEventSeries}
                    canNonMakeCosmeticChanges={eventsDomainStore.canMakeNonCosmeticChanges()}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={12}>
                  <EventBookingDeadlineEdit
                    isPreReleasedEventReleased={
                      isEditing && eventDetails.releaseDate !== undefined
                        ? eventDetails.releaseDate.date?.getTime()! <
                          currentTime
                        : false
                    }
                    value={eventDetails.bookingDetails}
                    eventPaidType={eventDetails.priceDetails.paidType.text}
                    eventStartDate={
                      eventDetails.eventDateDetails.startDate.date
                    }
                    onChange={(weeksChanged: boolean) => {
                      eventDetails.setReleaseDateFromBookingDeadline(false);
                      if (weeksChanged) {
                        eventDetails.setMaxDeadlineSelectValues(
                          eventDetails.eventDateDetails.startDate.date
                        );
                      }
                    }}
                  />
                </Col>
                <Col />
              </Row>
              <EventPreReleaseEdit
                value={eventDetails.releaseDate}
                eventDetails={eventDetails}
                isEditing={isEditing}
              />
            </FadeContent>
          )}
        </>
      )}

      {isEnhanced && (
        <>
          <Row className="form-separator editMargin" />
          <Row>
            <Col className="editMargin eventDescription">
              <TextEdit
                type="textarea"
                value={eventDetails.description}
                validationMessage={t("event:errors.description")}
                label={t("event:labels.description")}
                placeholder={t("event:placeholders.description")}
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <ImageSelector
                imageLoading={imageSelect.isPending}
                value={eventDetails.image}
                validationMessage={getImageValidationMessage()}
                label={t("event:labels.image")}
                onImageSelect={async (file) => await imageSelect.run(file)}
                isRequired={false}
              />
            </Col>
            <Col />
          </Row>
        </>
      )}
    </div>
  );

  const formatDate = (date: Date | undefined | null): string => {
    if (date) return format(date, "dd MMMM");
    return "";
  };

  /*
    An immutable state which is set on load of event data
    This lets us know if the event is bookable in the backend,
    regardless of the state in the form.
    This is necessary because the backend needs to know that an event is bookable
    Before it will allow bookings to be created.
    Therefore we must not show the bookings table if the event is not bookable on the backend.
  */
  const [isBookable] = useState(eventDetails.bookingDetails.hasBookingDeadline);

  const showBookingsTab = isEditing && isBookable && isEnhanced;

  useEffect(() => {
    if (showBookingsTab) {
      loadEventBookingDetails?.(eventDetails.id);
    } else {
      eventsUiStore.resetEventBookings();
    }
  }, [
    eventDetails.id,
    eventsUiStore,
    loadEventBookingDetails,
    showBookingsTab
  ]);

  const bookingsTabName = t("event:titles.manageBookings");
  const tabs: Tab[] = [
    {
      name: eventDetails.isPassedEndDate
        ? t("event:titles.viewEvent")
        : t("event:titles.editEvent"),
      content: formBody,
      testId: "eventDetailsTab"
    },
    {
      name: bookingsTabName,
      children: (
        <>
          {bookingsTabName}{" "}
          {EventDetailsHelper.isCancelledOrPostponedEvent(eventDetails) &&
            eventDetails.hasPaidAttendees && <Icon source={IconSource.error} />}
        </>
      ),
      content: <ManageBookingsTemplate />,
      testId: "manageBookingsTab"
    }
  ];

  const eventSeriesTab: Tab = {
    name: t("event:titles.editEventSeries"),
    content: formBody
  };

  const getEventTitle = (): string => {
    if (isEditing && eventsDomainStore.occurrenceDate) {
      return `${eventDetails?.title.text} - ${formatDate(
        utcToZonedTime(
          new Date(eventsDomainStore.occurrenceDate),
          eventsDomainStore.getTimeZone()
        )
      )}`;
    }

    if (isEditing) return eventDetails?.title.text;

    return t("event:titles.create");
  };

  const getFormBody = (): JSX.Element => {
    if (eventsUiStore.isViewingEventSeries()) {
      return formBody;
    }

    const isEditingEventSeries = params.get(QueryParams.EditSeries);
    if (isEditingEventSeries) {
      return <Tabs tabs={[eventSeriesTab]} />;
    }

    if (showBookingsTab) {
      return <Tabs tabs={tabs} />;
    }

    return formBody;
  };

  const breadcrumbTitle = t("event:titles.calendars", {
    name: eventsUiStore.selectedCalendarName
  });

  const breadcrumbItem: BreadcrumbItem = {
    name: breadcrumbTitle,
    onClick: () => Navigation.go(history, RoutePaths.Events),
    child: {name: getEventTitle()}
  };

  const haloColor = eventsUiStore.calendarColor;
  const haloIcon = eventsUiStore.calendarIcon;

  return (
    <div className="create-edit-event-template">
      <CreateEditForm>
        <CreateEditForm.Header
          breadcrumbItem={breadcrumbItem}
          hasUnsavedChanges={hasUnsavedChanges}
          haloColor={haloColor}
          iconSource={haloIcon}
        >
          {buttons}
        </CreateEditForm.Header>
        <CreateEditForm.Body>
          {isEditing && (
            <div data-testid="event-banner" className="heading-title d-flex">
              {isViewingOrEditingSeries && (
                <div>
                  {EventDetailsHelper.getRecurringEventStatusIcon(eventDetails)}
                </div>
              )}
              {eventDetails.title.text}
              {!isViewingOrEditingSeries && (
                <span data-testid={"event-startDate"}>
                  {formatDate(
                    eventDetails.eventDateDetails.startDate.date &&
                      utcToZonedTime(
                        eventDetails.eventDateDetails.startDate.date,
                        eventsUiStore.getTimeZone()
                      )
                  )}
                </span>
              )}
              <EventCreateEditPillView
                eventDetails={eventDetails}
                isEditingEventSeries={eventsUiStore.isEditingEventSeries}
                isEditingEvent={isEditing}
                currentDateTime={eventsDomainStore.getCurrentTime()}
                isCurrentEventReleased={eventsUiStore.isCurrentEventReleased()}
              />
              {eventDetails.isPassedEndDate &&
                eventDetails.releaseDate.date &&
                isEnhanced && (
                  <Tag
                    text={t("event:labels.calendar.preReleased")}
                    type="digital"
                  />
                )}
            </div>
          )}
          {getFormBody()}
        </CreateEditForm.Body>
      </CreateEditForm>
    </div>
  );
};

export default observer(CreateEditEventTemplate);
